import { useEffect } from "react";
import { ArrowRight, Check, MapPin, Phone } from "lucide-react";

import { LuxuryVisionBackdrop } from "@/components/site/LuxuryVisionBackdrop";
import { QuoteForm } from "@/components/site/QuoteForm";
import { SectionHeader } from "@/components/site/SectionHeader";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { findBcCoverageCity, getCoverageLanguageForCity } from "@/config/bc-coverage";
import { business } from "@/config/business";
import type { ServiceArea } from "@/config/service-areas";
import { serviceBySlug } from "@/config/services";
import { localizeServicePage, type PageUi } from "@/i18n/content";
import { useI18n, type LocaleCode } from "@/i18n";
import { trackAnalyticsEvent } from "@/lib/analytics";

import type { LocalizedPathBuilder } from "./ServiceAreasIndexPage";

type ServiceAreaPageProps = {
  area: ServiceArea;
  buildPath?: LocalizedPathBuilder;
  contactPath?: string;
  ui?: PageUi | undefined;
};

const identityPath: LocalizedPathBuilder = (path) => path;

function serviceNameForPath(servicePath: string, locale: LocaleCode): string {
  const slug = servicePath.replace(/^\/services\//, "").replace(/^\//, "");
  const service = serviceBySlug[slug];
  const localized = service ? localizeServicePage(service, locale) : undefined;
  return localized?.shortTitle ?? localized?.title ?? "Roofing service";
}

export function ServiceAreaPage({
  area,
  buildPath = identityPath,
  contactPath = "/contact",
  ui,
}: ServiceAreaPageProps) {
  const { locale } = useI18n();
  const coverageCity = findBcCoverageCity(area.slug);
  const coverageLanguage = coverageCity ? getCoverageLanguageForCity(coverageCity.id) : undefined;
  const showEmergencyCta = Boolean(
    coverageCity?.existingServiceArea && coverageCity.emergencyEligible,
  );

  useEffect(() => {
    trackAnalyticsEvent({
      event: "city_page_viewed",
      city: area.slug,
      locale,
    });
  }, [area.slug, locale]);

  const copy = ui?.serviceArea;
  const province =
    locale === "fa-ir" ? "بریتیش کلمبیا" : locale === "ar-sa" ? "كولومبيا البريطانية" : "BC";
  const localizedRegion =
    area.region === "Metro Vancouver"
      ? (copy?.regions.metroVancouver ?? area.region)
      : (copy?.regions.lowerMainland ?? area.region);
  const areaText = (template: string | undefined, fallback: string, prepend = false) => {
    const value = template ?? fallback;
    if (value.includes("{area}")) return value.replaceAll("{area}", area.name);
    return prepend ? `${area.name} ${value}` : `${value}${area.name}`;
  };

  return (
    <>
      <section className="relative isolate overflow-hidden bg-slate-deep pt-32 pb-16 text-primary-foreground md:pt-40 md:pb-24">
        <LuxuryVisionBackdrop rotationKey={`service-area:${area.slug}`} priority />
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-[linear-gradient(105deg,oklch(0.16_0.06_255/0.97)_0%,oklch(0.16_0.06_255/0.84)_55%,oklch(0.16_0.06_255/0.46)_100%)]"
        />
        <div className="container-page">
          <nav
            aria-label={ui?.serviceDetail.breadcrumbLabel ?? "Breadcrumb"}
            className="text-xs text-primary-foreground/55"
          >
            <a href={buildPath("/")} className="underline-offset-4 hover:underline">
              {ui?.common.home ?? "Home"}
            </a>
            <span aria-hidden="true" className="mx-2">
              /
            </span>
            <a href={buildPath("/service-areas")} className="underline-offset-4 hover:underline">
              {ui?.common.serviceAreas ?? "Service areas"}
            </a>
            <span aria-hidden="true" className="mx-2">
              /
            </span>
            <span aria-current="page">{area.name}</span>
          </nav>

          <div className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,1fr)_19rem] lg:items-end">
            <div className="max-w-3xl">
              <p className="eyebrow">
                {localizedRegion} {copy?.roofingServicesSuffix ?? "roofing services"}
              </p>
              <h1 className="display-1 mt-5 text-balance">
                {areaText(copy?.titlePrefix, "Roofing services in ")}, {province}
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-primary-foreground/72 md:text-lg">
                {area.intro}
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button variant="accent" size="xl" asChild>
                  <a
                    href="#area-quote"
                    onClick={() =>
                      trackAnalyticsEvent({
                        event: "estimate_cta_click",
                        cta_location: "city_hero_assessment",
                        lead_type: "planned",
                      })
                    }
                  >
                    {ui?.common.requestAssessment ?? "Request a Roof Assessment"}
                  </a>
                </Button>
                {showEmergencyCta ? (
                  <>
                    <Button variant="outlineDark" size="xl" asChild>
                      <a
                        href={buildPath("/emergency-roofing")}
                        onClick={() =>
                          trackAnalyticsEvent({
                            event: "emergency_cta_click",
                            cta_location: "city_hero_emergency",
                            lead_type: "emergency",
                          })
                        }
                      >
                        Emergency roofing
                      </a>
                    </Button>
                    <Button variant="outlineDark" size="xl" asChild>
                      <a
                        href={business.phoneHref}
                        onClick={() => {
                          trackAnalyticsEvent({
                            event: "emergency_cta_click",
                            cta_location: "city_hero_phone",
                            lead_type: "emergency",
                          });
                          trackAnalyticsEvent({
                            event: "phone_click",
                            link_location: `city_hero_${area.slug}`,
                          });
                        }}
                      >
                        <Phone aria-hidden="true" /> {ui?.common.call ?? "Call"} {business.phone}
                      </a>
                    </Button>
                  </>
                ) : null}
                <Button variant="outlineDark" size="xl" asChild>
                  <a href={buildPath("/services")}>
                    {ui?.common.exploreServices ?? "Explore roofing services"}
                  </a>
                </Button>
              </div>
              <a
                href={buildPath("/roof-estimate")}
                className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary-foreground/78 underline decoration-accent/60 underline-offset-4 hover:text-accent"
                onClick={() =>
                  trackAnalyticsEvent({
                    event: "estimate_cta_click",
                    cta_location: "city_hero_estimate_page",
                    lead_type: "planned",
                  })
                }
              >
                Request a planned roof estimate
                <ArrowRight className="size-4" aria-hidden="true" />
              </a>
            </div>

            <aside className="border-l border-primary-foreground/20 pl-6">
              <MapPin className="size-5 text-accent" aria-hidden="true" />
              <p className="mt-4 text-sm leading-relaxed text-primary-foreground/65">
                {areaText(
                  copy?.confirmedCommunity,
                  "is a confirmed service community. This is a service-area page, not a claim of a local branch office.",
                  true,
                )}
              </p>
              {coverageLanguage?.serviceAreaDisclaimer ? (
                <p className="mt-4 text-sm leading-relaxed text-primary-foreground/65">
                  {coverageLanguage.serviceAreaDisclaimer}
                </p>
              ) : null}
            </aside>
          </div>
        </div>
      </section>

      {coverageLanguage && (coverageLanguage.emergency || coverageLanguage.planned) ? (
        <section className="border-b border-border bg-background">
          <div className="container-page grid gap-px bg-border md:grid-cols-2">
            {coverageLanguage.emergency ? (
              <div className="bg-muted/50 p-7 md:p-9">
                <p className="text-xs font-semibold tracking-[0.14em] uppercase text-muted-foreground">
                  Emergency availability
                </p>
                <p className="mt-3 text-sm leading-relaxed text-foreground">
                  {coverageLanguage.emergency}
                </p>
              </div>
            ) : null}
            {coverageLanguage.planned ? (
              <div className="bg-muted/50 p-7 md:p-9">
                <p className="text-xs font-semibold tracking-[0.14em] uppercase text-muted-foreground">
                  Planned project availability
                </p>
                <p className="mt-3 text-sm leading-relaxed text-foreground">
                  {coverageLanguage.planned}
                </p>
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      <section className="section-y">
        <div className="container-page grid gap-12 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:gap-20">
          <div>
            <p className="eyebrow">{copy?.localContext ?? "Local roof context"}</p>
            <h2 className="display-2 mt-3 text-balance">
              {copy?.propertyDecides ?? "The property decides the recommendation"}
            </h2>
            <p className="mt-5 leading-relaxed text-muted-foreground">{area.localContext}</p>

            <div className="mt-9 rounded-lg border border-border bg-muted/50 p-6">
              <p className="text-xs font-semibold tracking-[0.14em] uppercase text-muted-foreground">
                {copy?.propertyContexts ?? "Property contexts we plan around"}
              </p>
              <ul className="mt-4 space-y-3">
                {area.propertyContexts.map((context) => (
                  <li key={context} className="flex gap-3 text-sm text-foreground">
                    <Check className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden="true" />
                    {context}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div>
            <p className="eyebrow">{copy?.assessmentPriorities ?? "Assessment priorities"}</p>
            <h2 className="display-3 mt-3">
              {areaText(copy?.attentionPrefix, "What deserves close attention in ")}
            </h2>
            <ol className="mt-7 divide-y divide-border border-y border-border">
              {area.assessmentFocus.map((item, index) => (
                <li key={item} className="grid grid-cols-[2.5rem_1fr] gap-4 py-6">
                  <span className="font-display text-lg text-accent">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <p className="leading-relaxed text-foreground">{item}</p>
                </li>
              ))}
            </ol>

            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
              {copy?.planningDisclaimer ??
                "This is general planning information, not a remote diagnosis or engineering opinion. Findings and applicable requirements are confirmed for the specific property."}
            </p>
          </div>
        </div>
      </section>

      <section className="section-y border-y border-border bg-muted/50">
        <div className="container-page">
          <SectionHeader
            eyebrow={copy?.relatedServices ?? "Related services"}
            title={areaText(copy?.decisionsPrefix, "Roofing decisions for ")}
            body={
              copy?.relatedBody ??
              "Start with the problem and observed roof condition. The right next step may be a focused repair, preventive assessment or a planned replacement."
            }
          />

          <div className="mt-10 grid gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-3">
            {area.relatedServicePaths.map((path) => (
              <a
                key={path}
                href={buildPath(path)}
                className="group flex min-h-44 flex-col justify-between bg-card p-6 transition-colors hover:bg-muted/60 focus-visible:bg-muted/60"
              >
                <h3 className="display-3 text-foreground">
                  {serviceNameForPath(path, locale) || copy?.genericService || "Roofing service"}
                </h3>
                <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium">
                  {ui?.common.learnMore ?? "Learn more"}
                  <ArrowRight
                    className="size-4 transition-transform group-hover:translate-x-1"
                    aria-hidden="true"
                  />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="section-y">
        <div className="container-page grid gap-12 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,1.25fr)]">
          <SectionHeader
            eyebrow={`${area.name} ${copy?.faqSuffix ?? "FAQ"}`}
            title={copy?.faqTitle ?? "Questions before an assessment"}
          />

          <Accordion type="single" collapsible className="w-full">
            {area.faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`area-faq-${index}`}>
                <AccordionTrigger className="text-left text-[0.98rem] font-medium">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section id="area-quote" className="section-y scroll-mt-20 bg-slate-deep">
        <div className="container-page grid gap-12 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16">
          <div>
            <p className="eyebrow">{copy?.requestAssessment ?? "Request an assessment"}</p>
            <h2 className="display-2 mt-3 text-balance text-primary-foreground">
              {copy?.assessmentTitle ?? "Start with the roof in front of us."}
            </h2>
            <p className="mt-5 max-w-lg leading-relaxed text-primary-foreground/68">
              {areaText(copy?.assessmentBodyPrefix, "Tell us about the property and roof area: ")}
            </p>
            <Button variant="outlineDark" size="xl" asChild className="mt-8">
              <a href={buildPath(contactPath)}>
                {copy?.contactOptions ?? "View all contact options"}
              </a>
            </Button>
            <a
              href={buildPath("/roof-estimate")}
              className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-primary-foreground/78 underline decoration-accent/60 underline-offset-4 hover:text-accent"
              onClick={() =>
                trackAnalyticsEvent({
                  event: "estimate_cta_click",
                  cta_location: "city_form_estimate_page",
                  lead_type: "planned",
                })
              }
            >
              Prefer a dedicated estimate page? Open the roof estimate form
              <ArrowRight className="size-4" aria-hidden="true" />
            </a>
          </div>
          <QuoteForm />
        </div>
      </section>
    </>
  );
}
